/* VV size finder - recommends a best size + an alternative from height, weight, body shape and fit preference.
   Sources, best first: garment measurements (product metafield custom.size_measurements),
   then height/weight ranges (custom.size_ranges, or the theme default). Product fit (shopify.fit or title)
   and the model info (custom.model_info) are used to calibrate. Everything is an estimate. */
(() => {
  const SizeFinder = {};

  /* ---------- parsing ---------- */
  const norm = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, '')
    .replace(/^XXXXL$/, '4XL').replace(/^XXXL$/, '3XL').replace(/^XXL$/, '2XL').replace(/^XXS$/, '2XS');
  const num = (s) => { const n = parseFloat(String(s).replace(',', '.')); return isNaN(n) ? null : n; };

  SizeFinder.parseRanges = (text) => String(text || '').split(/\r?\n/).map((line) => {
    const m = line.match(/^\s*([^:=]+?)\s*[:=]\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*(?:cm)?\s*[,;\/ ]+\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/i);
    return m ? { name: m[1].trim(), hMin: +m[2], hMax: +m[3], wMin: +m[4], wMax: +m[5] } : null;
  }).filter(Boolean);

  const DIM_ALIASES = [
    ['chest', /chest|poitrine|bust|pit|aisselle/],
    ['waist', /waist|ceinture|tour de taille/],
    ['hip', /hip|hanche|bassin|seat/],
    ['inseam', /inseam|entrejambe|inside leg/],
    ['length', /length|longueur|long/],
    ['shoulder', /shoulder|epaule|épaule/],
    ['sleeve', /sleeve|manche/],
    ['thigh', /thigh|cuisse/],
    ['leg', /leg|ouverture|bas/]
  ];
  const dimOf = (h) => { h = h.toLowerCase(); const f = DIM_ALIASES.find(([, re]) => re.test(h)); return f ? f[0] : null; };

  SizeFinder.parseMeasurements = (text) => {
    let lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    let mode = null;
    lines = lines.filter((l) => {
      if (/^(flat|à plat|a plat|half)$/i.test(l)) { mode = 'flat'; return false; }
      if (/^(full|circumference|tour|complet)$/i.test(l)) { mode = 'full'; return false; }
      return true;
    });
    if (lines.length < 2) return null;
    const sep = (l) => (/[;\t|]/.test(l) ? l.split(/[;\t|]/) : l.split(',')).map((c) => c.trim());
    const header = sep(lines[0]);
    const cols = header.map((h, i) => (i === 0 ? 'size' : dimOf(h)));
    const sizes = lines.slice(1).map((l) => {
      const cells = sep(l);
      if (!cells[0]) return null;
      const dims = {};
      cols.forEach((c, i) => { if (i > 0 && c && num(cells[i]) !== null) dims[c] = num(cells[i]); });
      return Object.keys(dims).length ? { name: cells[0], dims } : null;
    }).filter(Boolean);
    if (!sizes.length) return null;
    // circumference dims: convert flat (half) widths to full circumference
    const FLAT_LIMIT = { chest: 78, waist: 58, hip: 68, thigh: 40 };
    Object.keys(FLAT_LIMIT).forEach((d) => {
      const vals = sizes.map((s) => s.dims[d]).filter((v) => v != null);
      if (!vals.length) return;
      const isFlat = mode ? mode === 'flat' : Math.max(...vals) < FLAT_LIMIT[d];
      if (isFlat) sizes.forEach((s) => { if (s.dims[d] != null) s.dims[d] *= 2; });
    });
    return { sizes, cols: cols.slice(1).filter(Boolean), header: header.slice(1), raw: lines };
  };

  SizeFinder.parseModel = (text) => {
    const t = String(text || '');
    if (!t) return null;
    let h = null;
    const cm = t.match(/(\d{3})\s*cm/i);
    const m1 = t.match(/\b([12])[.,m]\s?(\d{2})\b/i);
    if (cm) h = +cm[1]; else if (m1) h = +(m1[1] + m1[2]);
    const kg = t.match(/(\d{2,3})\s*kg/i);
    const sz = t.match(/(?:size|taille|wears|wearing|porte)\s*(?:a|une|la|size|taille)?\s*:?\s*\b(XXS|XS|S|M|L|XL|XXL|XXXL|[2-4]XL|\d{2})\b/i);
    if (!h || !sz) return null;
    return { h, w: kg ? +kg[1] : null, size: sz[1] };
  };

  SizeFinder.fitFrom = (label, title) => {
    const s = (String(label || '') + ' | ' + String(title || '')).toLowerCase();
    if (/oversiz|surdimension|boxy|baggy|xxl fit/.test(s)) return 'oversize';
    if (/slim|ajust|skinny|fitted|muscle|près du corps|pres du corps|moulant/.test(s)) return 'slim';
    if (/relax|loose|large|ample|wide|décontract|decontract|straight leg|jogger/.test(s)) return 'relaxed';
    if (/regular|normal|classi|standard|droit/.test(s)) return 'regular';
    return null;
  };

  /* ---------- body estimate (cm) ---------- */
  SizeFinder.estimateBody = (h, w, gender, shape) => {
    const bmi = w / Math.pow(h / 100, 2);
    let b;
    if (gender === 'women') {
      b = {
        chest: 89 + (bmi - 22) * 2.0 + (h - 165) * 0.15,
        waist: 71 + (bmi - 22) * 1.8 + (h - 165) * 0.1,
        hip: 97 + (bmi - 22) * 2.1 + (h - 165) * 0.22
      };
    } else {
      b = {
        chest: 96 + (bmi - 22.9) * 1.9 + (h - 175) * 0.15,
        waist: 80 + (bmi - 22.9) * 1.7 + (h - 175) * 0.1,
        hip: 96 + (bmi - 22.9) * 1.5 + (h - 175) * 0.22
      };
    }
    const SHAPE = { slim: [-2, -3, -1], average: [0, 0, 0], broad: [4, 0, 1], belly: [1, 6, 2] }[shape] || [0, 0, 0];
    b.chest += SHAPE[0]; b.waist += SHAPE[1]; b.hip += SHAPE[2];
    b.topLength = h * 0.405;
    b.outseam = h * 0.6;
    b.inseam = h * 0.45;
    return b;
  };

  /* ---------- measurement mode ---------- */
  const EASE = {
    chest: { slim: 8, regular: 14, relaxed: 20, oversize: 28 },
    waist: { slim: 0, regular: 1, relaxed: 2, oversize: 3 },
    hip: { slim: 4, regular: 8, relaxed: 13, oversize: 18 }
  };
  const MIN_EASE = { chest: 2, waist: -3, hip: 1 };
  const TOL = { chest: 5, waist: 3, hip: 5, length: 5, inseam: 4 };
  const WEIGHT = { chest: 1, waist: 1.3, hip: 0.8, length: 0.35, inseam: 0.35 };
  const PREF = {
    closer: { chest: -6, waist: -2, hip: -4, length: -1, inseam: 0 },
    regular: { chest: 0, waist: 0, hip: 0, length: 0, inseam: 0 },
    looser: { chest: 8, waist: 3, hip: 6, length: 1, inseam: 0 }
  };

  const lengthTarget = (dims, body) => {
    if (dims.length == null) return null;
    const isBottom = dims.chest == null && (dims.waist != null || dims.hip != null || dims.inseam != null);
    if (isBottom) return dims.length >= 75 ? body.outseam : null; // skip shorts
    return body.topLength;
  };

  SizeFinder.byMeasurements = (meas, input, ctx) => {
    const body = SizeFinder.estimateBody(input.h, input.w, input.gender, input.shape);
    const fit = ctx.fit || 'regular';
    const targets = {};
    ['chest', 'waist', 'hip'].forEach((d) => { targets[d] = EASE[d][fit]; });
    targets.length = fit === 'oversize' ? 2 : 0;
    targets.inseam = 0;

    // calibrate on the model: the way the model wears it = the designed look
    let calibrated = false;
    if (ctx.model && ctx.model.w) {
      const ms = meas.sizes.find((s) => norm(s.name) === norm(ctx.model.size));
      if (ms) {
        const mb = SizeFinder.estimateBody(ctx.model.h, ctx.model.w, input.gender === 'women' && ctx.modelGender === 'women' ? 'women' : 'men', 'average');
        ['chest', 'waist', 'hip'].forEach((d) => {
          if (ms.dims[d] != null) targets[d] = 0.6 * (ms.dims[d] - mb[d]) + 0.4 * targets[d];
        });
        calibrated = true;
      }
    }
    const pref = PREF[input.pref] || PREF.regular;

    const scored = meas.sizes.map((s, i) => {
      let cost = 0;
      const detail = {};
      ['chest', 'waist', 'hip'].forEach((d) => {
        if (s.dims[d] == null) return;
        const ease = s.dims[d] - body[d];
        const target = targets[d] + pref[d];
        const diff = ease - target;
        const roomy = fit === 'relaxed' || fit === 'oversize' ? 0.6 : 1;
        let c = Math.pow(diff / TOL[d], 2) * (diff < 0 ? 2 : roomy);
        if (ease < MIN_EASE[d]) c += 6 * (MIN_EASE[d] - ease) / TOL[d];
        cost += c * WEIGHT[d];
        detail[d] = { garment: s.dims[d], body: body[d], ease };
      });
      const lt = lengthTarget(s.dims, body);
      if (lt != null) {
        const diff = s.dims.length - lt - (targets.length + pref.length);
        const isTop = s.dims.chest != null;
        cost += Math.pow(diff / TOL.length, 2) * WEIGHT.length * (isTop ? 1 : 0.45) * (diff < 0 && isTop ? 1.5 : 1);
      }
      if (s.dims.inseam != null) {
        const diff = s.dims.inseam - body.inseam - targets.inseam;
        cost += Math.pow(diff / TOL.inseam, 2) * WEIGHT.inseam * (diff < 0 ? 1.5 : 1);
      }
      return { i, name: s.name, cost, detail };
    });
    return { scored, body, calibrated, mode: 'measurements' };
  };

  /* ---------- range mode ---------- */
  const position = (v, centers) => {
    const n = centers.length;
    if (n === 1) return 0;
    if (v <= centers[0]) return Math.max(-0.6, (v - centers[0]) / (centers[1] - centers[0]));
    for (let i = 0; i < n - 1; i++) {
      if (v <= centers[i + 1]) return i + (v - centers[i]) / (centers[i + 1] - centers[i]);
    }
    return Math.min(n - 1 + 0.6, n - 1 + (v - centers[n - 1]) / (centers[n - 1] - centers[n - 2]));
  };

  SizeFinder.rangePos = (ranges, h, w) => {
    const ph = position(h, ranges.map((r) => (r.hMin + r.hMax) / 2));
    const pw = position(w, ranges.map((r) => (r.wMin + r.wMax) / 2));
    return 0.35 * ph + 0.65 * pw; // weight drives the size more than height
  };

  SizeFinder.byRanges = (ranges, input, ctx) => {
    let p = SizeFinder.rangePos(ranges, input.h, input.w);
    p += { slim: -0.15, average: 0, broad: 0.25, belly: 0.3 }[input.shape] || 0;
    if (!ctx.productRanges) p += { slim: 0.3, regular: 0, relaxed: -0.15, oversize: -0.35 }[ctx.fit] || 0;
    let calibrated = false;
    if (ctx.model && ctx.model.w) {
      const mi = ranges.findIndex((r) => norm(r.name) === norm(ctx.model.size));
      if (mi > -1) {
        const off = (mi - SizeFinder.rangePos(ranges, ctx.model.h, ctx.model.w)) * 0.5;
        p += Math.max(-0.6, Math.min(0.6, off));
        calibrated = true;
      }
    }
    p += { closer: -0.45, regular: 0, looser: 0.5 }[input.pref] || 0;
    const scored = ranges.map((r, i) => ({ i, name: r.name, cost: Math.pow(i - p, 2) * 4, detail: {} }));
    return { scored, p, calibrated, mode: 'ranges' };
  };

  /* ---------- pick best + alternative ---------- */
  SizeFinder.recommend = (data, input, offered) => {
    const ctx = { fit: data.fit, model: SizeFinder.parseModel(data.model), productRanges: !!data.productRanges };
    const meas = SizeFinder.parseMeasurements(data.measurements);
    const ranges = SizeFinder.parseRanges(data.productRanges || data.ranges);
    let res = null;
    if (meas && meas.sizes.some((s) => s.dims.chest != null || s.dims.waist != null || s.dims.hip != null)) {
      res = SizeFinder.byMeasurements(meas, input, ctx);
      if (ranges.length && data.productRanges) {
        const p = SizeFinder.rangePos(ranges, input.h, input.w);
        res.scored.forEach((s) => {
          const ri = ranges.findIndex((r) => norm(r.name) === norm(s.name));
          if (ri > -1) s.cost += Math.pow(ri - p, 2) * 0.6;
        });
      }
    } else if (ranges.length) {
      res = SizeFinder.byRanges(ranges, input, ctx);
    }
    if (!res) return null;
    let list = res.scored;
    if (offered && offered.length) {
      const o = offered.map(norm);
      const f = list.filter((s) => o.includes(norm(s.name)));
      if (f.length) list = f;
    }
    const sorted = [...list].sort((a, b) => a.cost - b.cost);
    const best = sorted[0];
    const bi = list.indexOf(best);
    const neighbours = [list[bi - 1], list[bi + 1]].filter(Boolean).sort((a, b) => a.cost - b.cost);
    let alt = neighbours[0] || null;
    // when the customer asked for looser/closer, the alternative goes the other way only if it is close
    if (alt && input.pref === 'looser' && list[bi + 1] && list[bi + 1].cost - best.cost < 3) alt = list[bi + 1];
    if (alt && input.pref === 'closer' && list[bi - 1] && list[bi - 1].cost - best.cost < 3) alt = list[bi - 1];
    const altDir = alt ? (list.indexOf(alt) > bi ? 'looser' : 'closer') : null;
    const gap = alt ? alt.cost - best.cost : 99;
    const confidence = gap > 2.5 ? 'high' : gap > 0.8 ? 'medium' : 'low';
    const edge = (bi === list.length - 1 && best.cost > 4) ? 'top' : (bi === 0 && best.cost > 4) ? 'bottom' : null;
    return { best, alt, altDir, confidence, edge, mode: res.mode, calibrated: res.calibrated, body: res.body, fit: ctx.fit, model: ctx.model, meas };
  };

  /* ---------- UI ---------- */
  const esc = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (map[k] != null ? map[k] : m));
  const STORE = 'vv-size-finder';
  const load = () => { try { return JSON.parse(localStorage.getItem(STORE) || '{}'); } catch (e) { return {}; } };
  const save = (v) => { try { localStorage.setItem(STORE, JSON.stringify(v)); } catch (e) { /* ignore */ } };

  const init = (box) => {
    if (box.dataset.vvSfBound) return;
    box.dataset.vvSfBound = 'true';
    let data;
    try { data = JSON.parse(box.querySelector('script[type="application/json"]').textContent); } catch (e) { return; }
    const T = data.strings || {};
    data.fit = SizeFinder.fitFrom(data.fitLabel, data.title);
    const fitLine = box.querySelector('[data-vv-sf-fitline]');
    if (fitLine && data.fit && T.fit_line) {
      fitLine.textContent = fill(T.fit_line, { fit: T['fit_' + data.fit] || data.fit });
      fitLine.hidden = false;
    }
    const dialog = box.closest('[data-vv-size-guide]');
    const getWrap = () => document.querySelector('[data-vv-vp="' + data.id + '"]');
    const sizeNames = String(data.optionNames || 'size,taille').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const sizeRadios = () => {
      const wrap = getWrap();
      if (!wrap) return [];
      const all = Array.from(wrap.querySelectorAll('input[type="radio"]'));
      const named = all.filter((r) => sizeNames.includes(String(r.dataset.optionName || '').trim().toLowerCase()));
      return named.length ? named : all;
    };
    const findRadio = (name) => sizeRadios().find((r) => norm(r.value) === norm(name));

    // measurement table when there is no chart image
    const table = (box.closest('[data-vv-size-guide]') || box.parentNode).querySelector('[data-vv-sf-table]');
    const meas = SizeFinder.parseMeasurements(data.measurements);
    if (table && meas) {
      const rawRows = meas.raw.slice(1).map((l) => (/[;\t|]/.test(l) ? l.split(/[;\t|]/) : l.split(',')).map((c) => c.trim()));
      table.innerHTML = '<p class="vv-sf__tabletitle">' + esc(T.table_title) + '</p><div class="vv-sf__tablewrap"><table class="vv-sf__table"><thead><tr><th>' +
        esc(T.size) + '</th>' + meas.header.map((h) => '<th>' + esc(h) + '</th>').join('') + '</tr></thead><tbody>' +
        rawRows.map((r) => '<tr>' + r.map((c, i) => (i === 0 ? '<th>' + esc(c) + '</th>' : '<td>' + esc(c) + '</td>')).join('') + '</tr>').join('') +
        '</tbody></table></div>';
    }

    const hIn = box.querySelector('[data-vv-sf-height]');
    const wIn = box.querySelector('[data-vv-sf-weight]');
    const out = box.querySelector('[data-vv-sf-result]');
    const state = Object.assign({ gender: data.defaultGender || 'men', shape: 'average', pref: 'regular' }, load());
    if (state.h) hIn.value = state.h;
    if (state.w) wIn.value = state.w;

    const syncChips = () => {
      box.querySelectorAll('[data-vv-sf-chip]').forEach((b) => {
        const on = state[b.dataset.group] === b.dataset.value;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };
    box.querySelectorAll('[data-vv-sf-chip]').forEach((b) => b.addEventListener('click', () => {
      state[b.dataset.group] = b.dataset.value;
      syncChips();
      if (!out.hidden) calc(false);
    }));
    syncChips();

    const card = (label, size, sub, cls) => {
      const r = findRadio(size);
      const sold = r && r.dataset.optionAvailable === 'false';
      return '<div class="vv-sf__card ' + cls + '">' +
        '<span class="vv-sf__size">' + esc(size) + '</span>' +
        '<span class="vv-sf__cardtext"><strong>' + esc(label) + '</strong>' + (sub ? '<small>' + sub + '</small>' : '') +
        (sold ? '<small class="vv-sf__sold">' + esc(T.sold_out) + '</small>' : '') + '</span>' +
        (r ? '<button type="button" class="vv-sf__select" data-vv-sf-select="' + esc(size) + '">' + esc(fill(T.select, { size })) + '</button>' : '') +
        '</div>';
    };
    const DIM_LABEL = { chest: T.chest, waist: T.waist, hip: T.hip };
    const reason = (rec, s) => {
      if (rec.mode !== 'measurements') return '';
      const order = ['chest', 'waist', 'hip'].filter((d) => s.detail[d]);
      if (!order.length) return '';
      const d = order[0];
      const x = s.detail[d];
      return esc(fill(T.reason, { part: DIM_LABEL[d], garment: Math.round(x.garment), body: Math.round(x.body) }));
    };

    const calc = (scroll) => {
      const h = parseFloat(hIn.value), w = parseFloat(wIn.value);
      out.hidden = false;
      if (!(h >= 120 && h <= 230 && w >= 30 && w <= 200)) {
        out.innerHTML = '<p class="vv-sf__err">' + esc(T.error) + '</p>';
        return;
      }
      Object.assign(state, { h, w });
      save(state);
      const offered = sizeRadios().map((r) => r.value);
      const rec = SizeFinder.recommend(data, { h, w, gender: state.gender, shape: state.shape, pref: state.pref }, offered);
      if (!rec) { out.innerHTML = '<p class="vv-sf__err">' + esc(T.no_data) + '</p>'; return; }
      const prefLabel = { closer: T.pref_closer, regular: T.pref_regular, looser: T.pref_looser }[state.pref];
      let html = card(fill(T.best, { fit: String(prefLabel).toLowerCase() }), rec.best.name, reason(rec, rec.best), 'is-best');
      if (rec.alt) html += card(rec.altDir === 'looser' ? T.alt_looser : T.alt_closer, rec.alt.name, reason(rec, rec.alt), 'is-alt');
      const notes = [];
      if (rec.edge === 'top') notes.push(T.note_top);
      else if (rec.edge === 'bottom') notes.push(T.note_bottom);
      else if (rec.confidence === 'low' && rec.alt) notes.push(fill(T.note_between, { a: rec.best.name, b: rec.alt.name }));
      if (rec.calibrated && rec.model) notes.push(fill(T.note_model, { height: rec.model.h, weight: rec.model.w || '', size: rec.model.size }));
      notes.push(rec.mode === 'measurements' ? T.note_measure : T.note_ranges);
      html += '<p class="vv-sf__notes">' + notes.filter(Boolean).map(esc).join('<br>') + '</p>';
      out.innerHTML = html;
      if (scroll) requestAnimationFrame(() => out.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
      out.querySelectorAll('[data-vv-sf-select]').forEach((btn) => btn.addEventListener('click', () => {
        const r = findRadio(btn.dataset.vvSfSelect);
        if (!r) return;
        r.click();
        btn.textContent = T.selected;
        setTimeout(() => { const c = dialog && dialog.querySelector('[data-vv-size-guide-close]'); if (c) c.click(); }, 450);
      }));
    };
    box.querySelector('[data-vv-sf-calc]').addEventListener('click', () => calc(true));
    [hIn, wIn].forEach((el) => el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); calc(true); } }));
  };

  SizeFinder.initAll = () => document.querySelectorAll('[data-vv-size-finder]').forEach(init);
  window.VVSizeFinder = SizeFinder;
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', SizeFinder.initAll);
    else SizeFinder.initAll();
    document.addEventListener('shopify:section:load', SizeFinder.initAll);
  }
})();
